import React from 'react';
import { loadDictionary, DictionaryConfig } from '../../utils/dictionary.ts';
import { GameInterface } from '../../types/GameInterface';
import { logDictionaryLoaded } from '../../utils/loggingUtils.ts';

interface WithDictionaryState {
  dictionary: Set<string> | null;
  dictionaryLoading: boolean;
}

// What DailyGame passes to HOC components (before dictionary loading)
interface GameWithDictionaryHOCProps extends Omit<GameInterface, 'dictionary'> {
  dictionaryConfig: DictionaryConfig;
  [key: string]: any;
} 

export default function GameWithDictionary<P extends GameInterface>(
  WrappedComponent: React.ComponentType<P>
) {
  return class extends React.Component<Omit<P, 'dictionary'> & GameWithDictionaryHOCProps, WithDictionaryState> {
    constructor(props: Omit<P, 'dictionary'> & GameWithDictionaryHOCProps) {
      super(props);
      this.state = {
        dictionary: null,
        dictionaryLoading: true,
      };
    }

    async componentDidMount(): Promise<void> {
      await this.loadDictionary();
    }

    async componentDidUpdate(prevProps: Omit<P, 'dictionary'> & GameWithDictionaryHOCProps): Promise<void> {
      // Reload dictionary if config changes
      const prevConfig = prevProps.dictionaryConfig;
      const currentConfig = this.props.dictionaryConfig;
      
      if (this.isDictionaryConfigChanged(prevConfig, currentConfig)) {
        this.setState({ dictionaryLoading: true });
        await this.loadDictionary();
      }
    }

    private isDictionaryConfigChanged(
      prev?: DictionaryConfig, 
      current?: DictionaryConfig
    ): boolean {
      if (!prev && !current) return false;
      if (!prev || !current) return true;
      return (
        prev.type !== current.type ||
        prev.version !== current.version ||
        prev.patch !== current.patch
      );
    }

    private async loadDictionary(): Promise<void> {
      const dictionaryConfig = this.props.dictionaryConfig;
      
      try {
        const dictionary = await loadDictionary(dictionaryConfig);
        
        // Log successful dictionary loading
        logDictionaryLoaded(
          dictionaryConfig.type,
          dictionaryConfig.version,
          dictionaryConfig.patch,
          dictionary.size,
          false // Not from cache since we're loading it fresh
        );
        
        this.setState({ dictionary, dictionaryLoading: false });
      } catch (error) {
        console.error('Failed to load dictionary:', error);
        this.setState({ dictionaryLoading: false });
        this.props.setErrorToast('Failed to load dictionary');
      }
    }

    render(): React.ReactNode {
      if (this.state.dictionaryLoading) {
        return (
          <div className="refresh-message">
            <div className="refresh-content">
              <div className="spinner" />
              <h2>Loading dictionary...</h2>
            </div>
          </div>
        );
      }

      if (!this.state.dictionary) {
        return (
          <div className="refresh-message">
            <div className="refresh-content">
              <h2>⚠️ Dictionary failed to load</h2>
              <p>Please refresh your browser to try again.</p>
              <button 
                className="refresh-button"
                onClick={() => window.location.reload()}
              >
                Refresh Now
              </button>
            </div>
          </div>
        );
      }

      // Pass all props except dictionaryConfig, and add dictionary
      const { dictionaryConfig, ...restProps } = this.props;
      
      return (
        <WrappedComponent
          {...(restProps as any)}
          dictionary={this.state.dictionary}
        />
      );
    }
  };
} 